import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["username"]
  static values  = { challengeUrl: String, verifyUrl: String }

  async authenticate() {
    const email = this.usernameTarget.value.trim()
    if (!email) { this.showError("Please enter your email."); return }
    this.clearError()

    try {
      // Get challenge from server
      const challengeRes = await fetch(this.challengeUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this.#csrfToken },
        body: JSON.stringify({ email })
      })
      const options = await challengeRes.json()
      if (challengeRes.status === 401) { window.location.href = options.redirect_to; return }
      if (!challengeRes.ok) { this.showError(options.error || "Sign in failed."); return }

      // Prepare options for browser
      options.challenge = b64ToBuffer(options.challenge)
      if (options.allowCredentials) {
        options.allowCredentials = options.allowCredentials.map(c => ({ ...c, id: b64ToBuffer(c.id) }))
      }

      // Prompt for passkey
      const credential = await navigator.credentials.get({ publicKey: options })

      // Send to server
      const verifyRes = await fetch(this.verifyUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this.#csrfToken },
        body: JSON.stringify(serializeGet(credential))
      })
      const result = await verifyRes.json()
      if (verifyRes.ok) {
        window.location.href = result.redirect_to
      } else {
        this.showError(result.error || "Verification failed.")
      }
    } catch (err) {
      if (err.name !== "NotAllowedError") this.showError(err.message)
    }
  }

  showError(msg) {
    const el = document.getElementById("webauthn-error")
    el.textContent = msg
    el.classList.remove("hidden")
  }

  clearError() {
    document.getElementById("webauthn-error")?.classList.add("hidden")
  }

  get #csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}

function b64ToBuffer(b64url) {
  const b64 = b64url.replace(/-/g, "+").replace(/_/g, "/")
  return Uint8Array.from(atob(b64), c => c.charCodeAt(0)).buffer
}

function bufferToB64(buffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "")
}

function serializeGet(credential) {
  return {
    type: credential.type,
    id: credential.id,
    rawId: bufferToB64(credential.rawId),
    response: {
      clientDataJSON:    bufferToB64(credential.response.clientDataJSON),
      authenticatorData: bufferToB64(credential.response.authenticatorData),
      signature:         bufferToB64(credential.response.signature),
      userHandle: credential.response.userHandle ? bufferToB64(credential.response.userHandle) : null
    }
  }
}
